<template>
  <UModal
    :open="modelValue"
    :fullscreen="isMobile"
    :ui="{
      footer: 'gap-0 p-0 sm:p-0',
    }"
    @update:open="emit('update:modelValue', $event)"
  >
    <template #body>
      <TText size="lg" variant="semibold" classes="mb-4">{{
        t('views.cottage-prices.extras.modal.detail')
      }}</TText>
      <div v-if="cottage && selectedRentUnits.length > 0" class="space-y-3">
        <div v-for="rentUnit in selectedRentUnits" :key="rentUnit.id">
          <CottagePricesSummaryModalSingle
            :name="rentUnit.name"
            :price="getRentUnitPriceFromStore(rentUnit.id)"
            :description="
              t(
                'views.cottage-prices.extras.modal.people',
                getRentUnitSelectedPeople(rentUnit.id),
              )
            "
          />
          <CottagePricesSummaryModalSingle
            v-for="extra in getRentUnitSelectedExtras(rentUnit.id)"
            :key="extra.id"
            :name="getExtraName(rentUnit.id, extra.id)"
            :price="extra.totalPrice"
            :description="getExtraDescription(rentUnit.id, extra)"
          />
        </div>
      </div>

      <TSeparator class="my-4" />

      <div class="grid grid-cols-12 gap-2 justify-between items-center">
        <div class="col-start-1 col-span-9 flex flex-row items-center gap-2">
          <TText size="lg" variant="semibold" classes="mt-0">{{
            t('views.cottage-prices.extras.modal.total')
          }}</TText>
          <TText size="sm" classes="mt-0">{{
            t('views.cottage-prices.extras.modal.totalIva')
          }}</TText>
        </div>
        <TText
          size="lg"
          variant="semibold"
          classes="text-right self-start col-start-10 col-span-3"
        >
          {{ toEuro(totalPrice) }}
        </TText>
      </div>
      <div
        v-if="pricesStore.isFreeCancellation"
        data-test="freeCancellationSection"
        class="bg-primary-100 rounded-lg overflow-hidden my-4 lg:mb-0 p-4"
      >
        <ul>
          <li class="flex gap-x-2">
            <UIcon
              name="i-heroicons-check-solid"
              class="w-5 text-green-600 h-6"
            ></UIcon>
            <TText size="sm" classes="mb-0">
              {{ t('views.cottage-prices.extras.modal.noPayment') }}
            </TText>
          </li>

          <li class="flex gap-x-2">
            <UIcon
              name="i-heroicons-check-solid"
              class="w-5 text-green-600 h-6"
            ></UIcon>
            <TText size="sm" classes="mb-0">
              <i18n-t
                keypath="views.cottage-prices.extras.modal.payDate.uptoDays"
                scope="global"
              >
                <template #freeCancellation>
                  <strong>
                    {{
                      t(
                        'views.cottage-prices.extras.modal.payDate.freeCancellation',
                      )
                    }}
                  </strong>
                </template>
                <template #n>{{
                  pricesStore.getFreeCancellationDays
                }}</template>
              </i18n-t>
            </TText>
          </li>
        </ul>
      </div>
    </template>
    <template #footer>
      <div v-if="hasOnlineBooking" class="p-3 sm:p-5 bg-gray-50 w-full">
        <OnlineBookingRowIcon
          :small-text="true"
          classes="flex gap-x-1 mb-0"
          :item="{
            labelIcon: 'clock',
            value: t('online-booking.resume.time'),
          }"
        />
      </div>
    </template>
  </UModal>
</template>
<script setup lang="ts">
import type { pricesStoreRentUnitExtraInterface } from '~~/modules/views/cottage-prices/stores/pricesStore'
import { usePricesStore } from '~~/modules/views/cottage-prices/stores/pricesStore'

defineProps<{
  modelValue: boolean
}>()
const emit = defineEmits(['update:modelValue'])

const { isMobile } = useDevice()
const pricesStore = usePricesStore()

// Get data from store using new structure
const cottage = computed(() => pricesStore.getCottageDetail)
const selectedRentUnits = computed(() => pricesStore.getSelectedRentUnits)
const totalPrice = computed(() => pricesStore.getTotalPrice || 0)

const hasOnlineBooking = computed(
  () => pricesStore.getCottageDetail?.salesService.isOnlineBookingActive,
)

function getRentUnitPriceFromStore(rentUnitId: string) {
  const rentUnitPrices = pricesStore.getRentUnitPricesById(rentUnitId)
  const rentUnitSelection = pricesStore.getRentUnitSelectionById(rentUnitId)

  if (!rentUnitPrices || !rentUnitSelection) return 0

  const priceData = rentUnitPrices.pricesByPlaces?.find(
    p => p.places === rentUnitSelection.selectedPeople,
  )
  return priceData?.price.amount || 0
}

function getRentUnitSelectedPeople(rentUnitId: string): number {
  const selection = pricesStore.getRentUnitSelectionById(rentUnitId)
  return selection?.selectedPeople || 0
}

function getRentUnitSelectedExtras(rentUnitId: string) {
  const selection = pricesStore.getRentUnitSelectionById(rentUnitId)
  return selection?.selectedExtras || []
}

function getExtraName(
  rentUnitId: number | string,
  extraId: number | string,
): string {
  const rentUnit = pricesStore.getRentUnitById(String(rentUnitId))
  if (!rentUnit) return ''
  const extra = rentUnit.extras?.find(e => e.id === extraId)
  return extra?.name || ''
}

function getExtraDescription(
  rentUnitId: number | string,
  extra: pricesStoreRentUnitExtraInterface,
): string {
  const rentUnit = pricesStore.getRentUnitById(String(rentUnitId))
  if (!rentUnit) return ''
  const originalExtra = rentUnit.extras?.find(e => e.id === extra.id)
  if (!originalExtra) return ''

  switch (extra.type) {
    case 'PEOPLE_DAY':
      return [
        t('views.cottage-prices.extras.modal.people', extra.people),
        t('views.cottage-prices.extras.modal.for'),
        t('views.cottage-prices.extras.modal.days', extra.days),
        t('views.cottage-prices.extras.modal.inRentUnit', {
          rentUnit: rentUnit.name,
        }),
      ].join(' ')
    case 'UNITY_DAY':
      return [
        t('views.cottage-prices.extras.modal.units', extra.units),
        t('views.cottage-prices.extras.modal.for'),
        t('views.cottage-prices.extras.modal.days', extra.days),
        t('views.cottage-prices.extras.modal.inRentUnit', {
          rentUnit: rentUnit.name,
        }),
      ].join(' ')
    case 'UNITY':
      return [
        t('views.cottage-prices.extras.modal.units', extra.units),
        t('views.cottage-prices.extras.modal.inRentUnit', {
          rentUnit: rentUnit.name,
        }),
      ].join(' ')
    case 'SINGLE_PAYMENT':
      return t('views.cottage-prices.extras.modal.singlePayment', {
        rentUnit: rentUnit.name,
      })
    default:
      return ''
  }
}
</script>
